/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { inject, Injectable } from '@angular/core';
import { Actions, Store } from '@ngxs/store';

import { Geometry as JstsGeometry } from 'jsts/org/locationtech/jts/geom';
import { OL3Parser } from 'jsts/org/locationtech/jts/io';
import { RelateOp } from 'jsts/org/locationtech/jts/operation/relate';
import { Feature } from 'ol';

import Collection from 'ol/Collection';
import { Extent } from 'ol/extent';
import GML32 from 'ol/format/GML32';
import { GeometryCollection, LineString, MultiLineString, MultiPoint, MultiPolygon, Point, Polygon } from 'ol/geom';
import LinearRing from 'ol/geom/LinearRing';
import { fromExtent } from 'ol/geom/Polygon';
import { Vector as VectorLayer } from 'ol/layer';
import { ProjectionLike } from 'ol/proj';
import { Vector as VectorSource } from 'ol/source';

import { combineLatest, EMPTY, forkJoin, Observable, of, throwError as _throw } from 'rxjs';
import {
    concatAll,
    concatMap,
    distinctUntilChanged,
    filter,
    first,
    map,
    reduce,
    shareReplay,
    switchMap,
    take,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { RedlineConfigService } from '../../../components/redline/services/redline-config.service';
import { RedlineLayerConfiguration } from '../../../components/redline/state/redline.model';
import { AppConfigState } from '../../../core/state/appConfig/app-config.state';
import { MapQueryFilter, MapQueryPropertyFilter, MapQuerySpatialFilter } from '../../../types/Map';
import { FeatureSelection } from '../../../types/MapFeature';

import { DashGisUtil } from '../../shared/dash-gis-util';
import { LoggerService } from '../../shared/services/logger.service';
import { RestService } from '../../shared/services/rest.service';
import { WFS } from '../ext/wfs.class';
import { SetLayerDisplayFeatures } from '../state/active-map.actions';
import { MsLayerNode } from '../state/map-config.model';
import { ClearSelection, MergeSelection } from '../state/selection.actions';
import { MergeSelectionPayload, SelectionFeaturePayload, SelectionFeatures } from '../state/selection.model';
import { SelectionState } from '../state/selection.state';
import { FeatureService } from './feature.service';
import { MapConfigService } from './map-config.service';
import { MapService, MapServiceFeatureSelectError } from './map.service';

@Injectable({ providedIn: 'root' })
export class QueryService {
    public hasSelection$: Observable<boolean> = inject(Store).select(SelectionState.hasSelection);
    private selection$: Observable<SelectionFeatures> = inject(Store).select(SelectionState.selection);

    constructor(
        private logger: LoggerService,
        private restService: RestService,
        private mapConfig: MapConfigService,
        private redlineConfig: RedlineConfigService,
        private mapService: MapService,
        private featureService: FeatureService,
        private actions$: Actions,
        private store: Store
    ) {
        // delay startup
        setTimeout(() => {
            combineLatest([this.selection$, this.mapService.mapAvailable$])
                .pipe(
                    filter(([_, available]) => available),
                    map(([selection, _]) => selection),
                    distinctUntilChanged()
                )
                .subscribe((selection: SelectionFeatures) => {
                    let featureIds = [];
                    for (const layer in selection) {
                        if (selection.hasOwnProperty(layer) && selection[layer].length > 0) {
                            const features = this.featureService.getFeatures(selection[layer]);
                            const style = this.getStyleForFeature('selection', features[0]);
                            this.featureService.updateFeatureStyle(selection[layer], style);
                            featureIds = [].concat([...featureIds, ...selection[layer]]);
                        }
                    }
                    this.store.dispatch(
                        new SetLayerDisplayFeatures({
                            displayLayer: 'selection',
                            featureIds,
                        })
                    );
                });
        }, 200);
    }

    public zoomTo(layerName: string, keyField: string, keyValue: any, otherZoomLayers: string[] = []) {
        of(true).pipe(
            concatMap(() => this.mapConfig.showLayers([layerName, ...otherZoomLayers], true)),
            concatMap(() => this.selectSingleFeature(layerName, keyField, keyValue, true)),
            take(1)
        ).subscribe()
    }

    public clearSelection() {
        this.store.dispatch(new ClearSelection());
    }

    public getSelection$(layerName: string, includeFeature = false) {
        return this.selection$.pipe(
            map((selection) => (selection && selection[layerName]) || []),
            map((featureIds: string[]) => this.featureService.getFeatures(featureIds)),
            map((features: Feature[]) =>
                features.map((feature) => {
                    const fieldNames = feature.getKeys();
                    const fields = {};
                    for (const fieldName of fieldNames) {
                        if (fieldName !== 'boundedBy' && fieldName !== 'msGeometry') {
                            fields[fieldName] = feature.get(fieldName);
                        }
                    }
                    const featureData: FeatureSelection = {
                        fields,
                        layer: layerName,
                        map: this.mapConfig.mapName,
                    };
                    if (includeFeature) {
                        featureData.feature = feature;
                    }
                    return featureData;
                })
            ),
            take(1)
        );
    }

    /**
     * Returns data only when a single Feature is selected on the selected layer,
     * or if no layer is provided, whichever layer does have a single feature selected.
     */
    public getSingleSelection$(layerName?: string, includeFeature = false): Observable<FeatureSelection> {
        return this.selection$.pipe(
            take(1),
            map((selection) => {
                if (layerName) {
                    return selection[layerName];
                }
                const potential = [];
                for (const layer in selection) {
                    if (selection[layer].length === 1) {
                        potential.push(layer);
                    }
                }
                if (potential.length === 1) {
                    layerName = potential[0];
                    return selection[potential[0]];
                }
                return [];
            }),
            map((featureIds) => this.featureService.getFeatures(featureIds)),
            map((features: Feature[]) => {
                if (features && features.length === 1) {
                    const feature = features[0];
                    const fieldNames = feature.getKeys();
                    const fields = {};
                    for (const fieldName of fieldNames) {
                        const olFields = ['boundedBy', 'geometry', 'msGeometry'];
                        if (olFields.lastIndexOf(fieldName) === -1) {
                            fields[fieldName] = feature.get(fieldName);
                        }
                    }
                    const featureData: FeatureSelection = {
                        fields,
                        layer: layerName,
                        map: this.mapConfig.mapName,
                    };
                    if (includeFeature) {
                        featureData.feature = feature;
                    }
                    return featureData;
                } else {
                    return null;
                }
            })
        );
    }

    public selectSingleFeature(layerName: string, keyField: string, keyValue: any, zoomTo = false) {
        let wmsLayers: any[] = [];
        let redlineLayers: string[] = [];

        return this.redlineConfig
            .getLayer$(layerName)
            .pipe(
                switchMap((redlineLayer) => {
                    if (redlineLayer) {
                        redlineLayers = [layerName];
                    } else {
                        wmsLayers = [this.mapConfig.getLayer(layerName)];
                    }
                    const propertyFilter: MapQueryPropertyFilter = {
                        filter: {
                            property: keyField,
                            values: [keyValue],
                        },
                        type: 'property',
                    };
                    return this.queryLayers$(
                        wmsLayers,
                        redlineLayers,
                        propertyFilter,
                        this.mapService.getVectorLayer('selection').getSource(),
                        true,
                        true,
                        zoomTo
                    );
                })
            );
    }

    /**
     * Query features based on map selection
     */
    public doQuery(queryFilter: MapQueryFilter, modifyExistingSelection: boolean, zoomTo = false, layerName?: string) {
        return this.redlineConfig.getVisibleLayerList$(this.mapService.currentScale).pipe(
            first(),
            map((visible: RedlineLayerConfiguration[]) => visible.map((layer) => layer.name)),
            map((redlineLayers: string[]) => {
                let layers: MsLayerNode[] = this.mapConfig.getSelectableLayers(this.mapService.currentScale);

                if (layerName) {
                    let singleLayer: MsLayerNode | string = layers.find((l) => l.name === layerName);
                    if (singleLayer) {
                        layers = [singleLayer as MsLayerNode];
                        redlineLayers = [];
                    } else {
                        singleLayer = redlineLayers.find((l) => l === layerName);
                        redlineLayers = !!singleLayer ? [singleLayer as string] : [];
                        layers = [];
                    }
                }
                return [layers, redlineLayers];
            }),
            switchMap(([layers, redlineLayers]: [MsLayerNode[], string[]]) => {
                if (redlineLayers.length > 0 || layers.length > 0) {
                    const selectionLayer = this.mapService.getVectorLayer('selection');
                    const source = selectionLayer.getSource();
                    return this.queryLayers$(
                        layers,
                        redlineLayers,
                        queryFilter,
                        source,
                        !modifyExistingSelection,
                        true,
                        zoomTo
                    );
                } else {
                    return EMPTY;
                }
            })
        );
    }

    /**
     *
     * @param {MsLayerNode[]} wmsLayers
     * @param {string[]} redlineLayers
     * @param {MapQueryFilter} queryFilter
     * @param {VectorSource} vectorSource
     * @param {boolean} clearExistingFeatures
     * @param {boolean} applyToSelection
     * @param {boolean} zoomTo
     * @return {Observable<void>}
     */
    public queryLayers$(
        wmsLayers: MsLayerNode[],
        redlineLayers: string[],
        queryFilter: MapQueryFilter,
        vectorSource: VectorSource,
        clearExistingFeatures = true,
        applyToSelection = true,
        zoomTo = false
    ): Observable<SelectionFeaturePayload[]> {
        if (vectorSource && clearExistingFeatures) {
            vectorSource.clear(true);
            // if (applyToSelection) {
            //     this.clearSelection();
            // }
        }
        const allFeatureStreams: Observable<SelectionFeaturePayload[]>[] = [];

        for (const redlineLayer of redlineLayers) {
            const redlineFeatures$ = this.getRedlineFeatureData$(redlineLayer, queryFilter).pipe(
                map((redlineFeatures) => redlineFeatures.filter((x) => !!x)),
                map((redlineFeatures) => [].concat.apply([], redlineFeatures)),
                map((redlineFeatures) => this.featureService.storeFeatures(redlineFeatures)),
                map((featureIds) => [{ layer: redlineLayer, featureIds }]),
                take(1)
            );
            allFeatureStreams.push(redlineFeatures$);
        }

        const layerNames = wmsLayers.map((l) => l?.name);

        const features$ = this.getWmsFeatureData$(layerNames, queryFilter).pipe(
            map((featureArrays) => featureArrays.filter((x) => !!x)),
            map((featureArrays) => [].concat.apply([], featureArrays)),
            concatAll(),
            map((wmsFeature: Feature) => this.featureService.storeFeature(wmsFeature)),
            map(
                (featureId: string): SelectionFeaturePayload => {
                    const parts = featureId.split('.');
                    if (parts.length === 2) {
                        return { layer: parts[0], featureIds: [featureId] };
                    }
                }
            ),
            filter((f) => !!f),
            reduce(
                (acc: SelectionFeaturePayload[], featureObject: SelectionFeaturePayload): SelectionFeaturePayload[] => {
                    const index = acc.findIndex((x) => x.layer === featureObject.layer);
                    if (index !== -1) {
                        acc[index].featureIds = acc[index].featureIds.concat(featureObject.featureIds);
                    } else {
                        acc.push(featureObject);
                    }
                    return acc;
                },
                []
            ),
            take(1)
        );
        allFeatureStreams.push(features$);

        // });
        const featureStream$ = forkJoin(allFeatureStreams).pipe(
            map((featureArrays): SelectionFeaturePayload[] => {
                const empty: SelectionFeaturePayload[] = [];
                return empty.concat.apply(empty, featureArrays);
            }),
            filter((featureArrays) => featureArrays.length > 0),
            map((featureArrays) => featureArrays.filter((x) => x.featureIds.length > 0)),
            shareReplay(1),
            take(1),
            tap((selectedFeatures) => {
                if (applyToSelection) {
                    const payload: MergeSelectionPayload = {
                        selectionFeatures: selectedFeatures,
                        clearSelection: clearExistingFeatures,
                    };
                    this.store.dispatch(new MergeSelection(payload));
                }
            })
        );

        if (zoomTo) {
            return featureStream$.pipe(
                tap((featureObjects: SelectionFeaturePayload[]) => {
                    const extents = [];
                    const featureIds = featureObjects.reduce(
                        (acc: string[], featureObject) => acc.concat.apply(acc, featureObject.featureIds),
                        []
                    );
                    for (const feature of featureIds) {
                        const extent = this.getFeatureExtent(feature);
                        extents.push(extent);
                    }
                    const fullExtent = this.mergeExtents(extents);
                    this.mapService.map.getView().fit(fullExtent, { size: this.mapService.map.getSize() });
                })
            );
        } else {
            return featureStream$;
        }
    }

    public selectWithinFeature$(features, mode): Observable<SelectionFeaturePayload[]> {
        mode = mode || 'Intersects';
        if (features.length > 1) {
            throw new MapServiceFeatureSelectError(
                'Multiple features found in selection layer. ' + 'You may only select within a single feature.'
            );
        }
        const selectionFeature = features[0];
        const spatialFilter: MapQuerySpatialFilter = {
            filter: {
                features: [selectionFeature],
                mode,
                type: 'geometry',
            },
            type: 'spatial',
        };

        const scale = this.mapService.currentScale;
        const selectableLayers$ = this.mapConfig.getSelectableLayers$(scale);

        return this.redlineConfig.getVisibleLayerList$(scale).pipe(
            map((redlineLayers) => redlineLayers.map((l) => l.name)),
            withLatestFrom(selectableLayers$),
            concatMap(([redlineLayers, selectableLayers]) =>
                this.queryLayers$(
                    selectableLayers,
                    redlineLayers,
                    spatialFilter,
                    this.mapService.getVectorLayer('selection').getSource(),
                    true,
                    true,
                    false
                )
            )
        );
    }

    public getRedlineFeatureData$(layerName: string, queryFilter: MapQueryFilter): Observable<Feature[]> {
        const layer = this.mapService.getVectorLayer(layerName);
        if (layer) {
            return this.redlineConfig.getLayer$(layerName).pipe(
                map((redlineLayer) => {
                    const layerKey = redlineLayer.key;
                    switch (queryFilter.type) {
                        case 'property':
                            return this.getRedlineFeaturesByProperty(queryFilter, layer, layerKey);
                        case 'spatial':
                            const spatialFilter = queryFilter as MapQuerySpatialFilter;
                            return this.getRedlineFeaturesBySpatialQuery(spatialFilter, layer);
                        default:
                            this.logger.error(
                                `Invalid filter type [${queryFilter.type}] specified in 'getRedlineFeatureData'`
                            );
                            return [];
                    }
                })
            );
        }

        return of([]);
    }

    public getWmsFeatureData$(layers: string[], queryFilter, geometryTypes: string[] = null): Observable<Feature[]> {
        const requestFilters = [];
        const maxFilterSize = 40;
        let filterOffset = 0;

        const buildRequestParams = (requestFilter, filterLayers) => {
            const filterParam = this.createXmlFilter(requestFilter, filterLayers);
            const params: any = {
                CRS: this.mapConfig.crs,
                // OutputFormat: 'gml3',
                Request: 'GetFeature',
                Service: 'WFS',
                TypeNames: Array.isArray(filterLayers) ? filterLayers.join(',') : filterLayers,
                Version: '2.0.0',
            };

            if (filterParam.type === 'FILTER') {
                // GetFeature requests with combined layers must send a filter statement for each layer
                const combinedFilterArray = [];

                filterLayers.forEach((l) => {
                    combinedFilterArray.push(`(${filterParam.filter})`);
                });
                params[filterParam.type] = combinedFilterArray.join('');
            } else {
                params[filterParam.type] = filterParam.filter;
            }

            if (geometryTypes) {
                params.geometryTypes = geometryTypes.join(',');
            }
            return params;
        };

        if (queryFilter.type === 'property') {
            const tempFilter = DashGisUtil.clone(queryFilter);
            while (filterOffset < queryFilter.filter.values.length) {
                tempFilter.filter.values = queryFilter.filter.values.slice(filterOffset, filterOffset + maxFilterSize);
                filterOffset += maxFilterSize;
                const params = buildRequestParams(tempFilter, layers);
                requestFilters.push(params);
            }
        } else {
            const params = buildRequestParams(queryFilter, layers);
            requestFilters.push(params);
        }

        const requests = [];
        for (const requestFilter of requestFilters) {
            const request$ = this.restService
                .apiPost<Feature[]>(`map/ows/${this.mapConfig.mapName}`, requestFilter, {}, { responseType: 'text' })
                .pipe(
                    take(1),
                    // TODO: verify type assignment
                    map((data: any) => {
                        if (data.indexOf('ExceptionReport') !== -1) {
                            const errorDoc = new DOMParser().parseFromString(data, 'text/xml');
                            this.logger.error(errorDoc);
                            return _throw('WFS Exception');
                        } else {
                            return data;
                        }
                    }),
                    map((data) => {
                        if (data) {
                            const featureTypes = layers.map((item) => `ms:${item}`);
                            const gmlFormat = new GML32({
                                featureNS: { ms: 'http://mapserver.gis.umn.edu/mapserver' },
                                featureType: featureTypes,
                                srsName: this.mapConfig.projection,
                            });
                            const geoFormat = new WFS({
                                featureNS: { ms: 'http://mapserver.gis.umn.edu/mapserver' },
                                featureType: featureTypes,
                                gmlFormat,
                                version: '2.0.0',
                            });
                            const features: Feature[] = geoFormat.readFeatures(data, {
                                dataProjection: this.mapConfig.projection as ProjectionLike,
                                featureProjection: null,
                            });
                            return features;
                        } else {
                            return null;
                        }
                    })
                );
            requests.push(request$);
        }

        return forkJoin(requests).pipe(
            map((featureArrays: any[]) => featureArrays.filter((item) => item !== null)),
            map((featureArrays: Feature[]) => [].concat.apply([], featureArrays))
        );
    }

    private filterFeaturesByFeature(vectorSource: VectorSource, filterFeature: Feature, mode: string) {
        const parser = new OL3Parser(undefined, undefined);
        parser.inject(
            Point,
            LineString,
            LinearRing,
            Polygon,
            MultiPoint,
            MultiLineString,
            MultiPolygon,
            GeometryCollection
        );
        const sourceFeatures = vectorSource.getFeatures();
        const geomArray = new Collection<Feature>();
        const filterGeometry = parser.read(filterFeature.getGeometry());
        sourceFeatures.forEach((feature) => {
            const geom = feature.getGeometry();
            if (geom) {
                const g = parser.read(geom);
                let match = false;
                switch (mode.toLowerCase()) {
                    case 'intersects':
                        match = RelateOp.intersects(g, filterGeometry);
                        break;
                    case 'contains':
                        match = RelateOp.contains(g, filterGeometry);
                        break;
                }
                if (match) {
                    geomArray.push(feature);
                }
            }
        });
        return geomArray;
    }

    private filterFeaturesByExtent(features: Feature[], bbox: Extent, mode = 'Intersects'): Feature[] {
        const parser = new OL3Parser(undefined, undefined);
        parser.inject(
            Point,
            LineString,
            LinearRing,
            Polygon,
            MultiPoint,
            MultiLineString,
            MultiPolygon,
            GeometryCollection
        );
        const geomArray = [];
        const filterGeometry = parser.read(fromExtent(bbox));
        features.forEach((feature) => {
            const g: JstsGeometry = parser.read(feature.getGeometry());
            let match = false;
            switch (mode.toLowerCase()) {
                case 'intersects':
                    match = RelateOp.intersects(g, filterGeometry);
                    break;
                case 'contains':
                    match = RelateOp.contains(g, filterGeometry);
                    break;
            }
            if (match) {
                geomArray.push(feature);
            }
        });
        return geomArray;
    }

    /**
     *
     * @param queryFilter {{type: string, filter: {Object}}}
     * @param layers {string[]} Array of layer names included in filter
     * @returns {*}
     */
    private createXmlFilter(queryFilter, layers) {
        if (queryFilter.type === 'spatial') {
            return this.createXmlSpatialFilter(queryFilter.filter);
        }

        if (queryFilter.type === 'property') {
            if (layers.length > 1) {
                throw new Error('Cannot execute property query on multiple layers');
            }
            return this.createXmlPropertyFilter(queryFilter.filter);
        }
    }

    private createXmlSpatialFilter(queryFilter) {
        let gml;
        let gmlData;
        let featureData;
        const mode = queryFilter.mode || 'Intersects';
        if (queryFilter.type === 'bbox') {
            return {
                filter: queryFilter.bbox.join(','),
                type: 'BBOX',
            };
        } else if (queryFilter.type === 'geometry') {
            gml = new GML32({
                featureNS: 'http://mapserver.gis.umn.edu/mapserver',
                // featureType: `${layers.join(',')}`,
                srsName: this.mapConfig.crs,
            });
            featureData = gml.writeFeaturesNode(queryFilter.features, { decimals: 3 });
            gmlData = this.extractGeometry(featureData);
            return {
                filter: `<Filter><${mode}>
                            <PropertyName>Geometry</PropertyName>
                            ${gmlData}
                        </${mode}></Filter>`,
                type: 'FILTER',
            };
        }
    }

    /**
     *
     * @param {Node} featureNode
     */
    private extractGeometry(featureNode) {
        // Chrome / FF
        const geom = this.findGeometryNode(featureNode);
        const geomStrings = [];
        const serializer = new XMLSerializer();
        for (const geomNode of geom.childNodes) {
            geomStrings.push(serializer.serializeToString(geomNode));
        }
        return geomStrings.join('');

        /*
         let doc = (new DOMParser()).parseFromString(featureNode.outerHTML, "text/xml");
         let geom = doc.evaluate(
         '//!*[name()="geometry"]/!*', featureNode, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null);
         return geom.singleNodeValue.outerHTML;
         */
    }

    private findGeometryNode(node) {
        if (node.nodeName === 'geometry') {
            return node;
        }
        if (node.childNodes) {
            for (const childNode of node.childNodes) {
                const result = this.findGeometryNode(childNode);
                if (result) {
                    return result;
                }
            }
        }
        return null;
    }

    /**
     * Create an XML string representing the property filter operation. Currently
     * only supports a single PROPERTY EQUALS VALUE form.
     * @param queryFilter {{property: string, values: string[]}}
     * @returns {{type: string, filter: string}}
     */
    private createXmlPropertyFilter(queryFilter) {
        const xmlProperty = `<PropertyName>${queryFilter.property}</PropertyName>`;
        const xmlValues = [];
        for (const filterValue of queryFilter.values) {
            const xmlValue = `<Literal>${filterValue}</Literal>`;
            xmlValues.push(`<PropertyIsEqualTo>${xmlProperty}${xmlValue}</PropertyIsEqualTo>`);
        }
        const xmlFilter = 1 < queryFilter.values.length ? `<OR>${xmlValues.join('')}</OR>` : xmlValues[0];
        return {
            filter: `<Filter>${xmlFilter}</Filter>`,
            type: 'FILTER',
        };
    }

    /**
     * Calculate the extent of the feature, taking into consideration
     * polygonzoommultiplier and pointminwidth config users
     * @param {string} featureId
     * @returns Extent
     */
    private getFeatureExtent(featureId: string): Extent {
        const feature = this.featureService.getFeature(featureId);
        const extent = feature.getGeometry().getExtent();
        const type = feature.getGeometry().getType();
        const newExtent: Extent = [extent[0], extent[1], extent[2], extent[3]];

        switch (type) {
            case 'Point':
                // apply pointminwidth
                const minWidth = this.store.selectSnapshot(AppConfigState.zoomGoto).pointminwidth;
                const w = extent[2] - extent[0];
                if (w < minWidth) {
                    const d = minWidth / 2;
                    newExtent[0] = newExtent[0] - d;
                    newExtent[1] = newExtent[1] - d;
                    newExtent[2] = newExtent[2] + d;
                    newExtent[3] = newExtent[3] + d;
                }
                return newExtent;

            default:
                // all other geometry types get the multiplier applied
                const multiplier = this.store.selectSnapshot(AppConfigState.zoomGoto).polygonzoommultiplier;
                const dx = ((newExtent[2] - newExtent[0]) * multiplier) / 2;
                const dy = ((newExtent[3] - newExtent[1]) * multiplier) / 2;
                newExtent[0] = newExtent[0] - dx;
                newExtent[1] = newExtent[1] - dy;
                newExtent[2] = newExtent[2] + dx;
                newExtent[3] = newExtent[3] + dy;
                return newExtent;
        }
    }

    private getRedlineFeaturesByProperty(queryFilter, layer: VectorLayer<VectorSource>, layerKey: string) {
        const propertyFilter = queryFilter as MapQueryPropertyFilter;
        const features = layer.getSource().getFeatures();
        return features.filter((item) => {
            let found = false;
            for (const value of propertyFilter.filter.values) {
                // layer key is not a property on features, so we have to use getId()
                if (propertyFilter.filter.property === layerKey) {
                    // PDO will in many many cases return a numeric value as a string
                    // eslint-disable-next-line eqeqeq
                    if (item.getId() == value) {
                        found = true;
                        break;
                    }
                } else {
                    if (item.get(propertyFilter.filter.property) === value) {
                        found = true;
                    }
                }
            }
            return found;
        });
    }

    private getRedlineFeaturesBySpatialQuery(spatialFilter: MapQuerySpatialFilter, layer: VectorLayer<VectorSource>) {
        switch (spatialFilter.filter.type) {
            case 'bbox':
                const testFeatures = layer.getSource().getFeaturesInExtent(spatialFilter.filter.bbox);
                return this.filterFeaturesByExtent(testFeatures, spatialFilter.filter.bbox);
            case 'geometry':
                const featureFeatures = this.filterFeaturesByFeature(
                    layer.getSource(),
                    spatialFilter.filter.features[0],
                    spatialFilter.filter.mode
                );
                return featureFeatures.getArray();
        }
    }

    private mergeExtents(extents: Extent[]): Extent {
        const fullExtent: Extent = [
            Number.POSITIVE_INFINITY,
            Number.POSITIVE_INFINITY,
            Number.NEGATIVE_INFINITY,
            Number.NEGATIVE_INFINITY,
        ];
        for (const extent of extents) {
            fullExtent[0] = Math.min(extent[0], fullExtent[0]);
            fullExtent[1] = Math.min(extent[1], fullExtent[1]);
            fullExtent[2] = Math.max(extent[2], fullExtent[2]);
            fullExtent[3] = Math.max(extent[3], fullExtent[3]);
        }
        return fullExtent;
    }

    private getStyleForFeature(styleCategory: string, feature: Feature) {
        return this.mapConfig.getStyles(styleCategory, this.mapService.getGeometryCategoryByFeature(feature));
    }
}
